import { FoodDto } from "../api";
import Food from "../data/food";
import { FoodUnit } from "../data/foodUnit";
import {
  DishTag,
  StepIngredientModification,
  StepModification,
} from "../data/dishModification";
import { emptyNutriValue, NutriValues } from "../data/nutriValues";
import { nutriValuesFromFoodUnitWithAmount } from "./planUtils";

export type DishNutri = NutriValues;

export const emptyNutri: DishNutri = emptyNutriValue;

export const addNutri = (a: DishNutri, b: DishNutri): DishNutri => ({
  fat: a.fat + b.fat,
  carbs: a.carbs + b.carbs,
  protein: a.protein + b.protein,
  kcal: a.kcal + b.kcal,
});

export const scaleNutri = (a: DishNutri, factor: number): DishNutri => ({
  fat: a.fat * factor,
  carbs: a.carbs * factor,
  protein: a.protein * factor,
  kcal: a.kcal * factor,
});

export const computeStepIngredientNutri = (
  ingredient: StepIngredientModification,
  foods: FoodDto[],
): DishNutri => {
  const nv = nutriValuesFromFoodUnitWithAmount(
    foods,
    ingredient.foodUnitId,
    ingredient.amount,
  );
  const fat = nv.fat ?? 0;
  const carbs = nv.carbs ?? 0;
  const protein = nv.protein ?? 0;
  const computedKcal = fat * 9 + carbs * 4 + protein * 4;
  return {
    fat,
    carbs,
    protein,
    kcal: Math.max(nv.kcal ?? 0, computedKcal),
  };
};

export const computeDishNutri = (
  steps: StepModification[],
  foods: FoodDto[],
): DishNutri =>
  steps.reduce(
    (acc, step) =>
      step.ingredients.reduce(
        (a, ing) =>
          addNutri(a, computeStepIngredientNutri(ing, foods)),
        acc,
      ),
    emptyNutri,
  );

/** Gesamtgewicht des Gerichts in Gramm (Menge × Nettogewicht der FoodUnit) */
export const computeDishWeightG = (
  steps: StepModification[],
  foods: FoodDto[],
): number =>
  steps.reduce(
    (acc, step) =>
      step.ingredients.reduce((a, ing) => {
        const food = foods.find((f) =>
          f.foodUnits.some((fu) => fu.id === ing.foodUnitId),
        );
        const foodUnit = food?.foodUnits.find((fu) => fu.id === ing.foodUnitId);
        return a + ing.amount * (foodUnit?.netWeight ?? 0);
      }, acc),
    0,
  );

/**
 * Berechnet, welche ernährungsbezogenen Tags automatisch zutreffen.
 * Regeln (identisch mit der Flyway-Migration):
 *   lowCalorie  : kcal pro 100g < 120
 *   lowCarb     : Kohlenhydrat-kcal < 25 % der Gesamt-kcal
 *   highProtein : Protein-kcal ≥ 25 % der Gesamt-kcal
 */
export const computeSuggestedNutriTags = (
  nutri: DishNutri,
  totalWeightG: number,
): DishTag[] => {
  if (nutri.kcal <= 0 || totalWeightG <= 0) return [];
  const tags: DishTag[] = [];
  if (nutri.kcal / (totalWeightG / 100) < 120) tags.push("lowCalorie");
  if ((nutri.carbs * 4) / nutri.kcal < 0.25) tags.push("lowCarb");
  if ((nutri.protein * 4) / nutri.kcal >= 0.25) tags.push("highProtein");
  return tags;
};

export const formatGrams = (n: number) => `${n.toFixed(1).replace(".", ",")} g`;
export const formatKcal = (n: number) => `${Math.round(n)}`;

/**
 * Leitet aus einem Gericht ein "Aus Gericht"-Lebensmittel ab. Die Nährwerte
 * werden – wie in der Dish-Tag-Migration – auf 100 g Gesamtgewicht normiert und
 * als einzige Einheit "Gericht" (Gesamtgewicht) hinterlegt.
 */
export const buildDishFood = (
  food: Food,
  dish: { description: string; steps: StepModification[] },
  foods: FoodDto[],
): Food => {
  const nutri = computeDishNutri(dish.steps, foods);
  const weight = computeDishWeightG(dish.steps, foods);
  const factor = weight > 0 ? weight : 1;
  const foodUnits: FoodUnit[] = [
    {
      id: -1,
      description: "Gericht",
      netWeight: weight,
      grossWeight: weight,
      isDefault: true,
      isAutoCreate: false,
    },
  ];
  return {
    ...food,
    description: dish.description,
    purchaseRelevant: false,
    fromDish: true,
    baseUnit: "G",
    fat: (nutri.fat * 100) / factor,
    carbs: (nutri.carbs * 100) / factor,
    protein: (nutri.protein * 100) / factor,
    kcal: (nutri.kcal * 100) / factor,
    foodUnits,
    availabilities: [],
    defaultPurchaseId: -1,
  };
};

